import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card } from 'react-bootstrap';
import elyisa_logo from '../../assets/Elysia-logo.svg';

interface ThinkingProps {
  thinkingText?: string;
  isLastBlockItem?: boolean;
  remarkPlugins?: any;
  rehypePlugins?: any;
  components?: any;
  showIcon?: boolean;
}

const Thinking: React.FC<ThinkingProps> = ({
  thinkingText = '',
  isLastBlockItem = false,
  remarkPlugins = [],
  rehypePlugins = [],
  components = {},
  showIcon = true,
}) => {
  const [isOpen, setIsOpen] = useState(isLastBlockItem);

  const sections = useMemo(() => {
    if (!thinkingText) return [];
    return thinkingText
      .split('===SECTION_BREAK===')
      .filter(Boolean)
      .map((sectionText) => {
        const lines = sectionText.split('\n');
        const blocks: string[] = [];
        let buffer: string[] = [];
        let insideBlock = false;
        for (const line of lines) {
          if (line.includes('block-start')) {
            insideBlock = true;
            buffer = [];
            continue;
          }
          if (line.includes('block-end')) {
            insideBlock = false;
            if (buffer.length) blocks.push(buffer.join('\n').trim());
            buffer = [];
            continue;
          }
          if (insideBlock) buffer.push(line);
        }
        if (insideBlock && buffer.length) blocks.push(buffer.join('\n').trim());
        return blocks.filter(Boolean);
      });
  }, [thinkingText]);

  const allBlocks = useMemo(() => sections.flat(), [sections]);

  useEffect(() => {
    setIsOpen(isLastBlockItem);
  }, [isLastBlockItem]);

  if (allBlocks.length === 0) return null;

  return (
    <div className="mb-3">
      <Card.Title>
        <div className="exclude-copy-response d-flex justify-content-between align-items-center">
          <div>
            {showIcon ? (
              <img className="sys-msg-card-img" src={elyisa_logo} alt="Elysia" />
            ) : (
              <div className="sys-msg-card-spacer" />
            )}
          </div>
          <button
            type="button"
            className="thinking-toggle-btn"
            onClick={() => setIsOpen((open) => !open)}
          >
            Thinking&nbsp;
            <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} />
          </button>
        </div>
      </Card.Title>

      {isOpen && (
        <div className="exclude-copy-response thinking-timeline">
          {allBlocks.map((block, index) => {
            const isLastBlock = index === allBlocks.length - 1;
            const isThinkingActive = isLastBlockItem && isLastBlock; // glowing dot only
            return (
              <div className="thinking-item" key={index}>
                <div className={`thinking-dot ${isThinkingActive ? 'thinking-dot--active' : ''}`} />
                {!isLastBlock && <span className="thinking-line" />}
                <div className="thinking-content">
                  <ReactMarkdown
                    remarkPlugins={remarkPlugins}
                    rehypePlugins={rehypePlugins}
                    components={components}
                  >
                    {block}
                  </ReactMarkdown>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Thinking;
